import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    SEQUENCE_PAD_COUNT,
    GameShell,
} from '../shared';
import { formatGameCopy } from './gameCopy';

function SequenceRepeatGame({ copy = {}, initialLevel, ...props }) {
    const startLevel = Math.max(1, Math.min(12, Math.round(Number(initialLevel) || 1)));
    const timersRef = useRef([]);
    const [sequence, setSequence] = useState([]);
    const [userIndex, setUserIndex] = useState(0);
    const [litPad, setLitPad] = useState(null);
    const [feedback, setFeedback] = useState({ pad: null, type: '' });
    const [playing, setPlaying] = useState(false);
    const [best, setBest] = useState(0);
    const [tone, setTone] = useState('ready');

    const clearTimers = useCallback(() => {
        timersRef.current.forEach((timer) => window.clearTimeout(timer));
        timersRef.current = [];
    }, []);

    const playSequence = useCallback((nextSequence) => {
        clearTimers();
        setPlaying(true);
        setUserIndex(0);
        setFeedback({ pad: null, type: '' });
        setTone('ready');

        nextSequence.forEach((pad, index) => {
            timersRef.current.push(window.setTimeout(() => setLitPad(pad), 520 * index));
            timersRef.current.push(window.setTimeout(() => setLitPad(null), 520 * index + 310));
        });

        timersRef.current.push(window.setTimeout(() => {
            setPlaying(false);
        }, Math.max(620, nextSequence.length * 520 + 180)));
    }, [clearTimers]);

    useEffect(() => () => clearTimers(), [clearTimers]);

    const start = () => {
        const nextSequence = Array.from(
            { length: startLevel },
            () => Math.floor(Math.random() * SEQUENCE_PAD_COUNT)
        );
        setSequence(nextSequence);
        setTone('ready');
        playSequence(nextSequence);
    };

    useEffect(() => {
        if (!sequence.length) {
            const timer = window.setTimeout(start, 260);
            return () => window.clearTimeout(timer);
        }

        return undefined;
    }, [sequence.length]);

    const extendSequence = (currentSequence) => {
        const nextSequence = currentSequence.length >= 12
            ? Array.from({ length: 12 }, () => Math.floor(Math.random() * SEQUENCE_PAD_COUNT))
            : [...currentSequence, Math.floor(Math.random() * SEQUENCE_PAD_COUNT)];
        setSequence(nextSequence);
        setBest((value) => Math.max(value, currentSequence.length));
        setTone('good');
        setPlaying(true);
        timersRef.current.push(window.setTimeout(() => playSequence(nextSequence), 620));
    };

    const pressPad = (pad) => {
        if (playing || !sequence.length) {
            return;
        }

        if (sequence[userIndex] !== pad) {
            setFeedback({ pad, type: 'wrong' });
            setTone('miss');
            setPlaying(true);
            timersRef.current.push(window.setTimeout(start, 820));
            return;
        }

        setFeedback({ pad, type: 'correct' });
        setTone('good');

        if (userIndex + 1 === sequence.length) {
            extendSequence(sequence);
            return;
        }

        setUserIndex((index) => index + 1);
        timersRef.current.push(window.setTimeout(() => {
            setFeedback({ pad: null, type: '' });
            setTone('ready');
        }, 360));
    };

    return (
        <GameShell
            {...props}
            copy={copy}
            metrics={{ level: Math.max(1, sequence.length), score: best }}
            tone={tone}
        >
            <div className="UFOV-brain-games__sequence-grid">
                {Array.from({ length: SEQUENCE_PAD_COUNT }, (_, index) => (
                    <button
                        className={`UFOV-brain-games__sequence-pad ${litPad === index ? 'is-lit' : ''} ${feedback.pad === index ? `is-${feedback.type}` : ''}`.trim()}
                        disabled={playing}
                        key={index}
                        type="button"
                        onClick={() => pressPad(index)}
                        aria-label={formatGameCopy(copy.answerOption, { number: index + 1 })}
                    >
                        <span aria-hidden="true" />
                    </button>
                ))}
            </div>
        </GameShell>
    );
}

export default SequenceRepeatGame;
